import './ApplianceSingle.scss';

import React from 'react';
import GatsbyImageWrapper from '../GatsbyImageWrapper';
import { useTranslation } from 'react-i18next';

import LocalePrice from '@progymedia/gatsby-theme-boutik-ecommerce/src/utils/LocalePrice';
import { useAvailabilityInfo } from '@progymedia/gatsby-theme-boutik-ecommerce/src/components/Cart/AddToCartButton';
import { usePageLocalization } from '@sbi-international/boutik.gatsby.plugin.multilocale';
import { Rating } from '@sbi-international/boutik.gatsby.plugin.components';
import { useProductPrice } from '@progymedia/gatsby-theme-boutik-ecommerce/src/context/store';
import { useProductReviews } from '@progymedia/gatsby-theme-boutik-ecommerce/src/components/Hooks/useProductReviews';
import { popFrontURL } from '../../utils';
import { IAppliance } from '../../interfaces';
import { DroletButton } from '../Primitives';

export default function ApplianceSingle(props: {
    appliance: IAppliance;
    id?: string;
    onTouchStart?: (e: any) => void;
    onTouchEnd?: (e: any) => void;
    isDiscontinued?: boolean;
}) {
    const { appliance, id, onTouchStart, onTouchEnd } = props;
    const { currentLocale, navigateTranslated } = usePageLocalization();
    const [price] = useProductPrice(appliance.code as string);
    const { t } = useTranslation('pages');

    const productReviews = useProductReviews(appliance.code);

    const goToSinglePage = () => {
        if (!props.isDiscontinued) {
            navigateTranslated(
                popFrontURL(appliance?.slug?.[currentLocale], 2)
            );
        } else {
            navigateTranslated(`/archives/${appliance?.code?.toLowerCase()}/`);
        }
    };

    const sizeText = appliance?.attributes?.size?.values?.[0]?.[currentLocale]
        ? '| ' + appliance?.attributes?.size?.values?.[0]?.[currentLocale]
        : '';
    const categoryAndSize =
        t(`layout:categories.${appliance?.category?.split('/')?.[3]}`) +
        ' ' +
        sizeText;
    const availabilityLabel = useAvailabilityInfo(
        appliance,
        true
    ).availabilityLabel;

    return (
        <div
            className='appliance-single'
            id={id || undefined}
            onTouchStart={(e) => onTouchStart && onTouchStart(e)}
            onTouchEnd={(e) => onTouchEnd && onTouchEnd(e)}
        >
            {appliance?.attributes?.certifications &&
                verifyEPA(appliance?.attributes?.certifications) && (
                    <div className='epa-tag'>EPA 2020</div>
                )}
            <div className='single-image' onClick={() => goToSinglePage()}>
                <GatsbyImageWrapper
                    className='product-img-wrapper'
                    fields={appliance.fields}
                />
            </div>
            <div className='single-content'>
                <span className='single-size'>{categoryAndSize}</span>
                <span className='single-title'>
                    {appliance?.title?.[currentLocale]}
                </span>
                <span className='line'></span>
                <span className='single-sku'>{appliance?.code}</span>
                <span className='single-price'>
                    {!props.isDiscontinued && RenderProductPrice()}
                </span>
                <span className='availability'>
                    {!props.isDiscontinued && availabilityLabel?.toUpperCase()}
                </span>
                <div className='rating'>
                    {!props.isDiscontinued &&
                        productReviews &&
                        productReviews.count > 0 && (
                            <Rating.RatingScore
                                ratingScore={productReviews?.average ?? 0}
                                ratingCount={productReviews?.count}
                            />
                        )}
                </div>
                <DroletButton
                    label={t('home.see-product')}
                    onClick={() => goToSinglePage()}
                />
            </div>
        </div>
    );

    function RenderProductPrice() {
        if (price?.regularPrice.value === price?.finalPrice.value) {
            return (
                <span className='normal-price'>
                    {LocalePrice(currentLocale, price?.regularPrice.value)}
                </span>
            );
        } else if (
            price?.regularPrice.value &&
            price?.finalPrice.value &&
            price.regularPrice.value !== price.finalPrice.value
        ) {
            return (
                <div className='discount-price'>
                    <span className='regular-price'>
                        {LocalePrice(currentLocale, price?.regularPrice.value)}
                    </span>
                    <span className='final-price'>
                        {LocalePrice(currentLocale, price?.finalPrice.value)}
                    </span>
                </div>
            );
        }
    }

    function verifyEPA(certifications: {
        values: { en_CA: string }[];
    }): boolean {
        return (
            certifications && certifications?.values?.[0]?.en_CA === 'EPA-2020'
        );
    }
}
